import { useState } from "react";
import Seo from "@/components/seo/Seo";
import { motion } from "framer-motion";
import { ArrowRight, Package, MapPin, User, Phone } from "lucide-react";
import PublicLayout from "@/components/layout/PublicLayout";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/utils/api";
import WhatsAppOptIn, { saveWhatsAppConsent } from "@/components/shared/WhatsAppOptIn";

export default function Booking() {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [waOptIn, setWaOptIn] = useState(false); // unticked by default

  const [form, setForm] = useState({
    origin_name: "", origin_address: "", origin_city: "", origin_pincode: "", origin_phone: "",
    dest_name: "", dest_address: "", dest_city: "", dest_pincode: "", dest_phone: "",
    weight: "", pkg_type: "parcel", description: "",
  });

  const [estimatedPrice, setEstimatedPrice] = useState(null);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const calculatePrice = async () => {
    if (!form.weight || !form.origin_pincode || !form.dest_pincode) return;
    try {
      const res = await axios.post(`${API}/pricing/calculate`, {
        weight: parseFloat(form.weight),
        origin_pincode: form.origin_pincode,
        destination_pincode: form.dest_pincode,
      });
      setEstimatedPrice(res.data.price);
    } catch {
      setEstimatedPrice(null);
    }
  };

  const handleNext = () => {
    if (step === 2) calculatePrice();
    setStep(step + 1);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("sendtoo_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const payload = {
        origin: {
          name: form.origin_name, address: form.origin_address,
          city: form.origin_city, pincode: form.origin_pincode, phone: form.origin_phone
        },
        destination: {
          name: form.dest_name, address: form.dest_address,
          city: form.dest_city, pincode: form.dest_pincode, phone: form.dest_phone
        },
        package: {
          weight: parseFloat(form.weight), type: form.pkg_type, description: form.description
        }
      };

      const res = await axios.post(`${API}/shipments`, payload, { headers });
      setResult(res.data);
      await saveWhatsAppConsent({ phone: form.origin_phone, consent: waOptIn, source: "public_booking", name: form.origin_name });
      setStep(4);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Booking failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PublicLayout>
      <Seo title={"Book a Shipment & Get a Quote"} description={"Book a courier, cargo or freight shipment with Sendtoo and get an indicative quote in minutes across South India and all of India."} path={"/book"} />
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <div className="bg-gradient-to-br from-[#0A183D] to-[#1B3FA0] py-12">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h1 className="font-heading text-4xl font-bold text-white uppercase">Book a Shipment</h1>
            <p className="text-blue-200 mt-2 text-sm">Fill in the details below to create your booking.</p>
          </div>
        </div>

        {/* Progress */}
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center gap-2 mb-8">
            {["Sender", "Receiver", "Package", "Confirm"].map((label, i) => (
              <div key={label} className="flex items-center gap-2 flex-1">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  step > i + 1 ? "bg-brand-500 text-white" : step === i + 1 ? "bg-brand-500 text-white ring-4 ring-brand-100" : "bg-slate-200 text-slate-500"
                }`}>{step > i + 1 ? "✓" : i + 1}</div>
                <span className={`text-xs font-semibold hidden sm:block ${step === i + 1 ? "text-brand-500" : "text-slate-400"}`}>{label}</span>
                {i < 3 && <div className={`flex-1 h-0.5 ${step > i + 1 ? "bg-brand-500" : "bg-slate-200"}`} />}
              </div>
            ))}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            {/* Step 1: Sender */}
            {step === 1 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading font-semibold text-xl text-slate-800 mb-6 flex items-center gap-2">
                  <User className="w-5 h-5 text-brand-500" /> Sender Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { name: "origin_name", label: "Full Name", placeholder: "Sender's full name" },
                    { name: "origin_phone", label: "Phone Number", placeholder: "+91 9876543210" },
                    { name: "origin_address", label: "Address", placeholder: "Street address", full: true },
                    { name: "origin_city", label: "City", placeholder: "Mumbai" },
                    { name: "origin_pincode", label: "PIN Code", placeholder: "400001" },
                  ].map((field) => (
                    <div key={field.name} className={field.full ? "sm:col-span-2" : ""}>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{field.label}</label>
                      <input
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        data-testid={`sender-${field.name}`}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 2: Receiver */}
            {step === 2 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading font-semibold text-xl text-slate-800 mb-6 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-brand-500" /> Receiver Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { name: "dest_name", label: "Full Name", placeholder: "Receiver's full name" },
                    { name: "dest_phone", label: "Phone Number", placeholder: "+91 9876543210" },
                    { name: "dest_address", label: "Address", placeholder: "Street address", full: true },
                    { name: "dest_city", label: "City", placeholder: "Delhi" },
                    { name: "dest_pincode", label: "PIN Code", placeholder: "110001" },
                  ].map((field) => (
                    <div key={field.name} className={field.full ? "sm:col-span-2" : ""}>
                      <label className="block text-xs font-semibold text-slate-600 mb-1.5">{field.label}</label>
                      <input
                        name={field.name}
                        value={form[field.name]}
                        onChange={handleChange}
                        placeholder={field.placeholder}
                        data-testid={`receiver-${field.name}`}
                        className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 focus:ring-1 focus:ring-brand-500"
                      />
                    </div>
                  ))}
                </div>
              </motion.div>
            )}

            {/* Step 3: Package */}
            {step === 3 && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <h2 className="font-heading font-semibold text-xl text-slate-800 mb-6 flex items-center gap-2">
                  <Package className="w-5 h-5 text-brand-500" /> Package Details
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Weight (kg)</label>
                    <input
                      type="number"
                      name="weight"
                      value={form.weight}
                      onChange={handleChange}
                      placeholder="0.5"
                      step="0.1"
                      min="0.1"
                      data-testid="package-weight"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Package Type</label>
                    <select
                      name="pkg_type"
                      value={form.pkg_type}
                      onChange={handleChange}
                      data-testid="package-type"
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 bg-white"
                    >
                      <option value="parcel">Parcel</option>
                      <option value="document">Document</option>
                      <option value="fragile">Fragile</option>
                      <option value="electronics">Electronics</option>
                      <option value="perishable">Perishable</option>
                    </select>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Description (optional)</label>
                    <textarea
                      name="description"
                      value={form.description}
                      onChange={handleChange}
                      placeholder="Brief description of the contents..."
                      rows={3}
                      className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500 resize-none"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <WhatsAppOptIn checked={waOptIn} onChange={setWaOptIn} testId="booking-whatsapp-optin" />
                </div>

                {estimatedPrice && (
                  <div className="mt-4 bg-brand-50 border border-brand-100 rounded-lg p-4 flex items-center justify-between">
                    <span className="text-sm font-semibold text-brand-600">Estimated Price</span>
                    <span className="font-heading text-2xl font-bold text-brand-500">₹{estimatedPrice}</span>
                  </div>
                )}
              </motion.div>
            )}

            {/* Step 4: Success */}
            {step === 4 && result && (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="font-heading text-2xl font-bold text-slate-800 mb-2">Booking Confirmed!</h2>
                <p className="text-slate-500 mb-6">Your shipment has been booked successfully.</p>
                <div className="bg-brand-50 rounded-lg p-4 inline-block">
                  <p className="text-xs text-brand-600 font-semibold uppercase mb-1">Tracking ID</p>
                  <p className="font-heading text-3xl font-bold text-brand-500">{result.tracking_id}</p>
                  <p className="text-sm text-slate-500 mt-1">Price: ₹{result.price}</p>
                </div>
                <p className="text-sm text-slate-500 mt-4">Save your tracking ID to track your shipment.</p>
                <button
                  onClick={() => { setStep(1); setForm({ origin_name: "", origin_address: "", origin_city: "", origin_pincode: "", origin_phone: "", dest_name: "", dest_address: "", dest_city: "", dest_pincode: "", dest_phone: "", weight: "", pkg_type: "parcel", description: "" }); setResult(null); setEstimatedPrice(null); }}
                  className="mt-6 px-6 py-2 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 text-sm uppercase"
                >
                  Book Another
                </button>
              </motion.div>
            )}

            {/* Navigation */}
            {step < 4 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-slate-100">
                {step > 1 ? (
                  <button
                    onClick={() => setStep(step - 1)}
                    className="px-6 py-2.5 border border-slate-200 text-slate-600 font-semibold rounded-sm hover:bg-slate-50 text-sm"
                  >
                    Back
                  </button>
                ) : <div />}
                {step < 3 ? (
                  <button
                    onClick={handleNext}
                    data-testid={`step-${step}-next`}
                    className="px-6 py-2.5 bg-brand-500 text-white font-semibold rounded-sm hover:bg-brand-600 text-sm flex items-center gap-2"
                  >
                    Next <ArrowRight className="w-4 h-4" />
                  </button>
                ) : (
                  <button
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="booking-submit-btn"
                    className="px-8 py-2.5 bg-[#F97316] text-white font-semibold rounded-sm hover:bg-orange-600 text-sm flex items-center gap-2 disabled:opacity-70"
                  >
                    {loading ? "Booking..." : "Confirm Booking"}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
